import React, { useMemo } from 'react';

function ds(date) {
  return date.toISOString().slice(0, 10);
}

function computeStreaks(entries) {
  if (!entries.length) return { log: 0, water: 0, routine: 0, longest: 0 };

  const entryByDate = {};
  entries.forEach(e => { entryByDate[e.date] = e; });

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  function countStreak(checkFn) {
    let streak = 0;
    const d = new Date(today);
    // Allow starting from yesterday if nothing logged today yet
    if (!entryByDate[ds(d)]) d.setDate(d.getDate() - 1);
    while (true) {
      const entry = entryByDate[ds(d)];
      if (!entry || !checkFn(entry)) break;
      streak++;
      d.setDate(d.getDate() - 1);
    }
    return streak;
  }

  const log = countStreak(() => true);
  const water = countStreak(e => (e.water || 0) >= 64);
  const routine = countStreak(e => e.routines?.AM?.length > 0 && e.routines?.PM?.length > 0);

  // Longest ever consecutive log streak
  const dates = [...new Set(entries.map(e => e.date))].sort();
  let longest = 0, cur = 0, lastMs = null;
  for (const dateStr of dates) {
    const ms = new Date(dateStr + 'T00:00:00').getTime();
    if (lastMs === null) {
      cur = 1;
    } else {
      const diff = Math.round((ms - lastMs) / 86400000);
      cur = diff === 1 ? cur + 1 : 1;
    }
    longest = Math.max(longest, cur);
    lastMs = ms;
  }

  return { log, water, routine, longest };
}

function Badge({ emoji, value, label, color }) {
  return (
    <div className="flex flex-col items-center flex-1 py-4 px-2">
      <span className="text-2xl mb-1.5">{emoji}</span>
      <span className={`font-serif text-2xl leading-none font-medium ${color}`}>{value}</span>
      <span className="font-sans text-xs text-stone-400 text-center mt-1 leading-tight">{label}</span>
    </div>
  );
}

export default function StreakCard({ entries }) {
  const { log, water, routine, longest } = useMemo(() => computeStreaks(entries), [entries]);

  const message = log === 0
    ? 'Log today to start your streak!'
    : log === 1
    ? 'Log tomorrow to keep it going!'
    : `${log} days strong — keep it up!`;

  return (
    <div className="bg-white rounded-3xl border border-stone-100 shadow-sm overflow-hidden">
      <div className="flex divide-x divide-stone-100">
        <Badge emoji="🔥" value={log} label={`Day${log !== 1 ? 's' : ''} Logged`} color="text-orange-500" />
        <Badge emoji="💧" value={water} label="Water Goal" color="text-blue-500" />
        <Badge emoji="🧴" value={routine} label="Full Routine" color="text-teal-600" />
      </div>
      <div className="px-5 py-2.5 bg-stone-50 border-t border-stone-100 flex items-center justify-between">
        <p className="font-sans text-xs text-stone-400">{message}</p>
        {longest > 1 && (
          <p className="font-sans text-xs text-stone-500 font-semibold">Best: {longest}d 🏆</p>
        )}
      </div>
    </div>
  );
}
